import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import { photoGallery, grouped } from '../../helpers/photoGallery';
import {
  LuCamera, LuUser, LuUsers, LuCalendarDays, LuPawPrint, LuBriefcase, LuContrast,
  LuLayoutGrid, LuPanelsTopLeft, LuX, LuChevronLeft, LuChevronRight,
} from 'react-icons/lu';
import './style.css';

/* ── Category definitions (labels resolved via t() in component) ── */
const CATEGORY_DEFS = [
  { id: 'all',        Icon: LuCamera },
  { id: 'portrait',   Icon: LuUser },
  { id: 'family',     Icon: LuUsers },
  { id: 'events',     Icon: LuCalendarDays },
  { id: 'animals',    Icon: LuPawPrint },
  { id: 'business',   Icon: LuBriefcase },
  { id: 'blackwhite', Icon: LuContrast },
];

/* ── Lightbox ───────────────────────────────────────────────────── */
const Lightbox = ({ photos, initialIndex, onClose }) => {
  const [index, setIndex] = useState(initialIndex);
  const [dir, setDir] = useState(0);

  const go = useCallback((d) => {
    setDir(d);
    setIndex((prev) => (prev + d + photos.length) % photos.length);
  }, [photos.length]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const handle = (e) => {
      if (e.key === 'Escape')      onClose();
      if (e.key === 'ArrowLeft')   go(-1);
      if (e.key === 'ArrowRight')  go(1);
    };
    window.addEventListener('keydown', handle);
    return () => {
      window.removeEventListener('keydown', handle);
      document.body.style.overflow = '';
    };
  }, [go, onClose]);

  const slide = {
    enter: (d) => ({ x: d > 0 ? 60 : -60, opacity: 0, scale: 0.97 }),
    center:      { x: 0, opacity: 1, scale: 1 },
    exit:  (d) => ({ x: d > 0 ? -60 : 60, opacity: 0, scale: 0.97 }),
  };

  return (
    <motion.div
      className="mpg-lb"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.22 }}
      onClick={onClose}
    >
      <motion.button
        className="mpg-lb__close"
        onClick={onClose}
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15 }}
        aria-label="Close"
      >
        <LuX />
      </motion.button>

      <motion.div
        className="mpg-lb__counter"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
      >
        {index + 1} <span>/</span> {photos.length}
      </motion.div>

      <button
        className="mpg-lb__nav mpg-lb__nav--prev"
        onClick={(e) => { e.stopPropagation(); go(-1); }}
        aria-label="Previous photo"
      >
        <LuChevronLeft />
      </button>

      <AnimatePresence mode="wait" custom={dir}>
        <motion.div
          key={index}
          className="mpg-lb__img-wrap"
          custom={dir}
          variants={slide}
          initial="enter"
          animate="center"
          exit="exit"
          transition={{ duration: 0.32, ease: [0.25, 0.46, 0.45, 0.94] }}
          onClick={(e) => e.stopPropagation()}
        >
          <img
            src={photos[index].src}
            alt={photos[index].title}
            className="mpg-lb__img"
          />
        </motion.div>
      </AnimatePresence>

      <button
        className="mpg-lb__nav mpg-lb__nav--next"
        onClick={(e) => { e.stopPropagation(); go(1); }}
        aria-label="Next photo"
      >
        <LuChevronRight />
      </button>
    </motion.div>
  );
};

/* ── Photo card ─────────────────────────────────────────────────── */
const PhotoCard = React.memo(({ photo, listIndex, onClick }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });
  const [loaded, setLoaded] = useState(false);

  return (
    <motion.figure
      ref={ref}
      className="mpg-card"
      initial={{ opacity: 0, y: 24 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{
        duration: 0.55,
        delay: (listIndex % 9) * 0.06,
        ease: [0.25, 0.46, 0.45, 0.94],
      }}
      onClick={() => onClick(listIndex)}
    >
      <div className={`mpg-card__img-wrap ${loaded ? 'mpg-card__img-wrap--loaded' : ''}`}>
        {!loaded && <div className="mpg-card__skeleton" />}
        <img
          src={photo.src}
          alt={photo.title}
          loading="lazy"
          onLoad={() => setLoaded(true)}
          className="mpg-card__img"
        />
        <div className="mpg-card__overlay">
          <span className="mpg-card__num">{String(photo.num).padStart(2, '0')}</span>
          <span className="mpg-card__zoom">&#x2B;</span>
        </div>
      </div>
    </motion.figure>
  );
});

const INITIAL_COUNT = 10;

/* ── Main component ─────────────────────────────────────────────── */
const ModernPhotoGallery = () => {
  const { t } = useTranslation('gallery');

  const [activeCategory, setActiveCategory] = useState('all');
  const [viewMode, setViewMode]             = useState('masonry');
  const [lightboxIdx, setLightboxIdx]       = useState(null);
  const [visibleCount, setVisibleCount]     = useState(INITIAL_COUNT);

  // For specific category — use pre-sorted grouped array (newest first)
  // For "all" — use the balanced photoGallery array
  const filtered = activeCategory === 'all'
    ? photoGallery
    : (grouped[activeCategory] ?? []);

  const visible    = filtered.slice(0, visibleCount);
  const hasMore    = visibleCount < filtered.length;
  const isExpanded = visibleCount > INITIAL_COUNT;

  const handleCatChange = (id) => {
    setActiveCategory(id);
    setVisibleCount(INITIAL_COUNT);
  };

  const openLightbox  = useCallback((idx) => setLightboxIdx(idx), []);
  const closeLightbox = useCallback(() => setLightboxIdx(null), []);

  // Accurate counts from grouped data
  const countFor = (id) =>
    id === 'all'
      ? photoGallery.length
      : (grouped[id]?.length ?? 0);

  const viewModes = [
    { mode: 'masonry', Icon: LuPanelsTopLeft, label: t('portfolio.viewMasonry') },
    { mode: 'grid',    Icon: LuLayoutGrid,    label: t('portfolio.viewGrid') },
  ];

  return (
    <div className="mpg">

      {/* ── Header ── */}
      <div className="mpg__header">
        <h2 className="title-2">
          {t('portfolio.title')} <span className="accent">{t('portfolio.titleAccent')}</span>
        </h2>
        <p className="mpg__desc">{t('portfolio.desc')}</p>
      </div>

      {/* ── Controls ── */}
      <div className="mpg__controls">

        {/* category tabs */}
        <div className="mpg__cats" role="tablist" aria-label="Filter by category">
          {CATEGORY_DEFS.map(({ id, Icon }) => (
            <button
              key={id}
              role="tab"
              aria-selected={activeCategory === id}
              className={`mpg__cat ${activeCategory === id ? 'mpg__cat--active' : ''}`}
              onClick={() => handleCatChange(id)}
            >
              <Icon className="mpg__cat-icon" />
              <span className="mpg__cat-label">{t(`portfolio.categories.${id}`)}</span>
              <span className="mpg__cat-count">{countFor(id)}</span>
            </button>
          ))}
        </div>

        {/* view toggle */}
        <div className="mpg__view-toggle" aria-label="Switch layout">
          {viewModes.map(({ mode, Icon, label }) => (
            <button
              key={mode}
              className={`mpg__view-btn ${viewMode === mode ? 'mpg__view-btn--active' : ''}`}
              onClick={() => setViewMode(mode)}
              title={label}
              aria-label={label}
            >
              <Icon />
            </button>
          ))}
        </div>

      </div>

      {/* ── Grid ── */}
      <AnimatePresence mode="wait">
        <motion.div
          key={activeCategory + viewMode}
          className={`mpg__grid mpg__grid--${viewMode}`}
          layout
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{
            opacity: { duration: 0.18 },
            layout: { duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] },
          }}
        >
          {visible.map((photo, i) => (
            <PhotoCard
              key={photo.id}
              photo={photo}
              listIndex={i}
              onClick={openLightbox}
            />
          ))}

          {filtered.length === 0 && (
            <div className="mpg__empty">
              <LuCamera size={40} />
              <p>{t('portfolio.empty')}</p>
            </div>
          )}
        </motion.div>
      </AnimatePresence>

      {/* ── Show more / Show less ── */}
      {filtered.length > INITIAL_COUNT && (
        <motion.div
          className="mpg__more"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          {isExpanded && (
            <button
              className="button mpg__more-btn mpg__more-btn--less"
              onClick={() => setVisibleCount(INITIAL_COUNT)}
            >
              {t('portfolio.showLess')}
            </button>
          )}
          {hasMore && (
            <button
              className="button mpg__more-btn"
              onClick={() => setVisibleCount((c) => c + 6)}
            >
              {t('portfolio.showMore')}
              <span className="mpg__more-count">+{filtered.length - visibleCount}</span>
            </button>
          )}
        </motion.div>
      )}

      {/* ── Lightbox ── */}
      <AnimatePresence>
        {lightboxIdx !== null && (
          <Lightbox
            photos={filtered}
            initialIndex={lightboxIdx}
            onClose={closeLightbox}
          />
        )}
      </AnimatePresence>

    </div>
  );
};

export default ModernPhotoGallery;